import React, { useCallback, useEffect, useState } from "react";
import {
  PrimaryButton,
  DefaultButton,
  IconButton,
  Stack,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Icon,
} from "@fluentui/react";
import { ReusableDialog } from "../../../components/popupModals/ReusableDialog";
import { getTitle } from "../../../functions/helperMethod";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(value) {
  return EMAIL_REGEX.test(String(value ?? "").trim().toLowerCase());
}

/**
 * Share dialog: enter one or more email addresses (type + Enter to add a chip),
 * choose a view/edit permission, then Copy link or Send.
 *
 * Presentational — the parent supplies the Graph-backed callbacks:
 *  - onCopyLink(recipients, role) => void      (recipients may be empty)
 *  - onSend(recipients, role, message) => void (requires recipients)
 */
export default function ShareDocumentDialog({
  isOpen,
  onClose,
  onCopyLink,
  onSend,
  documentTitle = "",
  isBusy = false,
  busyLabel = "",
  errorMessage = "",
  isCheckedOut = false,
  theme,
}) {
  const [inputValue, setInputValue] = useState("");
  const [chips, setChips] = useState([]);
  const [role, setRole] = useState("read");
  const [localError, setLocalError] = useState("");

  // Reset everything when the dialog (re)opens.
  useEffect(() => {
    if (isOpen) {
      setInputValue("");
      setChips([]);
      setRole("read");
      setLocalError("");
    }
  }, [isOpen]);

  const addChip = useCallback((person) => {
    const email = String(person?.email ?? "").trim().toLowerCase();
    if (!email) return;
    setChips((prev) =>
      prev.some((chip) => chip.email === email)
        ? prev
        : [...prev, { id: person.id || email, displayName: person.displayName || email, email }],
    );
    setInputValue("");
    setLocalError("");
  }, []);

  const addTypedEmail = useCallback(() => {
    const value = inputValue.trim();
    if (!value) return;
    if (!isValidEmail(value)) {
      setLocalError(`"${value}" is not a valid email address.`);
      return;
    }
    addChip({ id: value, displayName: value, email: value });
  }, [inputValue, addChip]);

  const removeChip = useCallback((email) => {
    setChips((prev) => prev.filter((chip) => chip.email !== email));
  }, []);

  const handleInputKeyDown = useCallback(
    (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        addTypedEmail();
      } else if (ev.key === "Backspace" && inputValue === "" && chips.length > 0) {
        setChips((prev) => prev.slice(0, -1));
      }
    },
    [addTypedEmail, inputValue, chips.length],
  );

  const recipients = chips.map((chip) => ({ email: chip.email }));

  // A checked-out document is locked in OneDrive, so it can't be edited in the
  // browser. Block "Can edit" sharing until it's checked in; viewing is fine.
  const editBlocked = isCheckedOut && role === "write";

  const handleCopyLink = useCallback(() => {
    if (isBusy || editBlocked) return;
    // A typed-but-not-yet-added email should still count.
    let effectiveRecipients = recipients;
    if (inputValue.trim() && isValidEmail(inputValue)) {
      effectiveRecipients = [
        ...recipients,
        { email: inputValue.trim().toLowerCase() },
      ];
    }
    onCopyLink?.(effectiveRecipients, role);
  }, [isBusy, editBlocked, recipients, inputValue, role, onCopyLink]);

  const handleSend = useCallback(() => {
    if (isBusy || editBlocked) return;
    let effectiveRecipients = recipients;
    if (inputValue.trim()) {
      if (!isValidEmail(inputValue)) {
        setLocalError(`"${inputValue.trim()}" is not a valid email address.`);
        return;
      }
      effectiveRecipients = [
        ...recipients,
        { email: inputValue.trim().toLowerCase() },
      ];
    }
    if (effectiveRecipients.length === 0) {
      setLocalError("Add at least one person to send to.");
      return;
    }
    onSend?.(effectiveRecipients, role, "");
  }, [isBusy, editBlocked, recipients, inputValue, role, onSend]);

  const rawTitle = getTitle(documentTitle || "document");
  const displayTitle =
    rawTitle.length > 40 ? `${rawTitle.slice(0, 40).trimEnd()}…` : rawTitle;
  const fontColor = theme?.colors?.fontColor;
  const isDarkMode =
    String(theme?.colors?.pureColor || "").toLowerCase() === "#000000";
  const border = `1px solid ${
    isDarkMode
      ? theme?.colors?.dividerColor ?? "rgba(255, 255, 255, 0.24)"
      : "rgb(220, 220, 220)"
  }`;
  const chipBg = theme?.colors?.contextMenuHover ?? "#E2EFDF";
  const themeDark = theme?.colors?.themeDarkColor;
  const dialogBg = theme?.colors?.popupDialogBackgroundColor;
  // Field background: darker neutral in dark mode, white in light.
  const fieldBg = isDarkMode
    ? "rgb(31, 31, 31)"
    : theme?.colors?.pureColor ?? "#fff";
  // Menu (dropdown / suggestions) colors — theme-aware so dark matches the app menus.
  const menuBg = theme?.colors?.contextMenuBackground ?? "#fff";
  const menuHoverBg = theme?.colors?.contextMenuHover ?? "#E2EFDF";
  // Font weight/size must live on the `label` slot — Fluent sets label weight to
  // semibold there, which is more specific than `root` and would otherwise win.
  const buttonLabel = {
    fontFamily: theme?.fonts?.regular,
    fontSize: "13px",
    fontWeight: 500,
  };

  // Match the app's standard dialog buttons (see ConfirmModal); explicit
  // hover/pressed colors so they never flash the Fluent grey/white default.
  const primaryDark = "#3E7457"; // darker green for hover/pressed
  const secondaryButtonStyles = {
    root: {
      height: 40,
      color: themeDark,
      borderRadius: "20px",
      borderColor: themeDark,
      backgroundColor: dialogBg ?? "transparent",
    },
    label: buttonLabel,
    icon: { color: themeDark },
    rootHovered: { backgroundColor: menuHoverBg, color: themeDark, borderColor: themeDark },
    rootPressed: { backgroundColor: menuHoverBg, color: themeDark, borderColor: themeDark },
    iconHovered: { color: themeDark },
    iconPressed: { color: themeDark },
    rootDisabled: {
      backgroundColor: dialogBg ?? "transparent",
      borderColor: themeDark,
      color: themeDark,
      opacity: 0.6,
    },
    labelDisabled: { color: themeDark },
    iconDisabled: { color: themeDark },
  };
  const primaryButtonStyles = {
    root: {
      height: 40,
      borderRadius: "20px",
      minWidth: "80px",
      backgroundColor: themeDark,
      borderColor: themeDark,
      color: "#FFFFFF",
    },
    label: { ...buttonLabel, color: "#FFFFFF" },
    icon: { color: "#FFFFFF" },
    rootHovered: { backgroundColor: primaryDark, borderColor: primaryDark, color: "#FFFFFF" },
    rootPressed: { backgroundColor: primaryDark, borderColor: primaryDark, color: "#FFFFFF" },
    iconHovered: { color: "#FFFFFF" },
    iconPressed: { color: "#FFFFFF" },
    rootDisabled: {
      backgroundColor: themeDark,
      borderColor: themeDark,
      color: "#FFFFFF",
      opacity: 0.6,
    },
    labelDisabled: { color: "#FFFFFF" },
    iconDisabled: { color: "#FFFFFF" },
  };

  const permissionMenuProps = {
    items: [
      {
        key: "write",
        text: "Can edit",
        iconProps: { iconName: "Edit" },
        canCheck: true,
        checked: role === "write",
        onClick: () => setRole("write"),
      },
      {
        key: "read",
        text: "Can view",
        iconProps: { iconName: "View" },
        canCheck: true,
        checked: role === "read",
        onClick: () => setRole("read"),
      },
    ],
    styles: {
      root: {
        border,
        selectors: {
          ".ms-ContextualMenu-itemText": { fontSize: 13, color: fontColor },
          // Check mark: force a high-contrast color so it's visible in dark mode.
          ".ms-ContextualMenu-checkmarkIcon": { color: `${fontColor} !important`, opacity: 1 },
          ".ms-ContextualMenu-link:hover": {
            backgroundColor: menuHoverBg,
            color: fontColor,
          },
          // Icons keep their default color, turning green only on hover.
          ".ms-ContextualMenu-link:hover .ms-ContextualMenu-icon": {
            color: themeDark,
          },
          ".ms-FocusZone, .ms-ContextualMenu, .is-open, .ms-BaseButton-menuhost": {
            backgroundColor: menuBg,
          },
        },
      },
    },
  };

  const actions = (
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 18 } }}>
      {localError ? (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          {localError}
        </MessageBar>
      ) : null}
      {errorMessage ? (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          {errorMessage}
        </MessageBar>
      ) : null}
      {editBlocked ? (
        <MessageBar messageBarType={MessageBarType.warning} isMultiline>
          This document is checked out, so it can&apos;t be shared for editing. Check it in first to share with edit access.
        </MessageBar>
      ) : null}
      <Stack
        horizontal
        verticalAlign="center"
        horizontalAlign="space-between"
        tokens={{ childrenGap: 8 }}
      >
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
          {isBusy ? (
            <>
              <Spinner size={SpinnerSize.small} />
              <span style={{ fontSize: 12, color: fontColor }}>
                {busyLabel || "Working…"}
              </span>
            </>
          ) : null}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DefaultButton
            iconProps={{ iconName: "Link" }}
            text="Copy link"
            onClick={handleCopyLink}
            disabled={isBusy || editBlocked}
            styles={secondaryButtonStyles}
          />
          <PrimaryButton
            iconProps={{ iconName: "Send" }}
            text="Send"
            onClick={handleSend}
            disabled={isBusy || editBlocked}
            styles={primaryButtonStyles}
          />
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <ReusableDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Share "${displayTitle}"`}
      showHeader
      isBlocking
      dialogMinHeight={0}
      dialogMinWidth="460px"
      dialogMaxWidth="min(92vw, 520px)"
      bodyMinHeight={0}
      bodyPadding="30px 24px 24px"
      actions={actions}
    >
      <Stack tokens={{ childrenGap: 10 }}>
        {/* People picker: chips + input + permission dropdown */}
        <div style={{ position: "relative" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border,
              borderRadius: 6,
              padding: "4px 6px",
              background: fieldBg,
            }}
          >
            <Icon
              iconName="Contact"
              styles={{ root: { color: fontColor, fontSize: 16, flexShrink: 0 } }}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                flex: 1,
                minWidth: 0,
                alignItems: "center",
              }}
            >
              {chips.map((chip) => (
                <span
                  key={chip.email}
                  title={chip.email}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: chipBg,
                    color: fontColor,
                    borderRadius: 12,
                    padding: "2px 6px 2px 8px",
                    fontSize: 12,
                    maxWidth: "100%",
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 160,
                    }}
                  >
                    {chip.displayName}
                  </span>
                  <Icon
                    iconName="Cancel"
                    onClick={() => removeChip(chip.email)}
                    styles={{
                      root: {
                        fontSize: 10,
                        cursor: "pointer",
                        color: fontColor,
                      },
                    }}
                  />
                </span>
              ))}
              <input
                value={inputValue}
                onChange={(ev) => {
                  setInputValue(ev.target.value);
                  if (localError) setLocalError("");
                }}
                onKeyDown={handleInputKeyDown}
                placeholder={
                  chips.length === 0 ? "Add an email address" : "Add more"
                }
                disabled={isBusy}
                style={{
                  flex: 1,
                  minWidth: 120,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: fontColor,
                  fontSize: 13,
                  padding: "4px 2px",
                }}
              />
            </div>
            <IconButton
              ariaLabel="Change permission"
              iconProps={{ iconName: role === "write" ? "Edit" : "View" }}
              menuProps={permissionMenuProps}
              disabled={isBusy}
              styles={{
                root: {
                  border: "none",
                  borderRadius: 0,
                  borderLeft: `1px solid ${theme?.colors?.dividerColor ?? "#C8C8C8"}`,
                  background: "transparent",
                  width: 58,
                  height: 30,
                  flexShrink: 0,
                  color: fontColor,
                  padding: "0 6px 0 14px",
                },
                rootHovered: { background: "transparent", color: fontColor },
                rootPressed: { background: "transparent", color: fontColor },
                rootExpanded: { background: "transparent", color: fontColor },
                flexContainer: { justifyContent: "center" },
                menuIcon: { fontSize: 10, color: fontColor },
              }}
            />
          </div>
        </div>

      </Stack>
    </ReusableDialog>
  );
}
